import { TableCell, TableRow } from '@/components/ui-kit/table';
import { cn } from '@/lib/utils';
import type { ContactsTableColumnDef } from '../../config/contacts-table-columns';
import {
  CONTACTS_TABLE_CHECKBOX_STICKY_CLASS,
  CONTACTS_TABLE_NAME_STICKY_CLASS,
  getContactsTableBodyCellClass,
} from '../../config/contacts-table-columns';

const ContactsTableSkeletonBlock = ({
  className,
}: {
  className?: string;
}) => (
  <span
    className={cn(
      'inline-block animate-pulse bg-[#EDEEF0]',
      className
    )}
    aria-hidden
  />
);

const renderSkeletonCell = (columnId: ContactsTableColumnDef['id']) => {
  switch (columnId) {
    case 'name':
      return (
        <div className="flex items-center gap-2.5">
          <ContactsTableSkeletonBlock className="size-8 rounded-full" />
          <ContactsTableSkeletonBlock className="h-3.5 w-[120px] rounded" />
        </div>
      );
    case 'email':
      return <ContactsTableSkeletonBlock className="block h-3.5 w-[150px] rounded" />;
    case 'phone':
      return <ContactsTableSkeletonBlock className="block h-5 w-20 rounded-full" />;
    case 'source':
      return <ContactsTableSkeletonBlock className="block h-5 w-[100px] rounded-md" />;
    case 'lists':
      return <ContactsTableSkeletonBlock className="block h-3.5 w-[90px] rounded" />;
    case 'created':
      return <ContactsTableSkeletonBlock className="block h-3.5 w-[70px] rounded" />;
    default:
      return <ContactsTableSkeletonBlock className="block h-3.5 w-24 rounded" />;
  }
};

type ContactsTableSkeletonRowsProps = {
  rowCount?: number;
  visibleColumns: ContactsTableColumnDef[];
};

export const ContactsTableSkeletonRows = ({
  rowCount = 8,
  visibleColumns,
}: ContactsTableSkeletonRowsProps) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <TableRow
        key={`contacts-skeleton-${index}`}
        className="border-b border-[#F4F5F6] hover:bg-transparent"
      >
        <TableCell
          className={cn(CONTACTS_TABLE_CHECKBOX_STICKY_CLASS, 'bg-white px-4 py-3 align-middle')}
        >
          <ContactsTableSkeletonBlock className="block size-[18px] rounded" />
        </TableCell>
        {visibleColumns.map((column) => (
          <TableCell
            key={column.id}
            className={cn(
              getContactsTableBodyCellClass(column.id, column.widthClass),
              column.id === 'name' && CONTACTS_TABLE_NAME_STICKY_CLASS
            )}
          >
            {renderSkeletonCell(column.id)}
          </TableCell>
        ))}
        <TableCell className="w-12 px-4 py-3" />
      </TableRow>
    ))}
  </>
);
